import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Link2 } from 'lucide-react';

import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { Button } from '../../components/ui/button';
import { CompactTable } from '../../components/ui/CompactTable';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { api } from '../../services/api';
import { legacyApi, type Customer } from '../../services/legacyApi';
import type { ArReceiptSummary } from '../../services/types';
import { toast } from 'sonner';

const formatMoney = (value: number) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatReceiptNumber = (receiptId?: number | null) =>
    Number(receiptId || 0) > 0 ? `HC/RCPT/${String(Number(receiptId || 0)).padStart(4, '0')}` : '-';

const formatInvoiceNumber = (invoiceId?: number | null) =>
    Number(invoiceId || 0) > 0 ? `HC/INV/${String(Number(invoiceId || 0)).padStart(4, '0')}` : '-';

const formatSoNumber = (soId?: number | null) =>
    Number(soId || 0) > 0 ? `HC/SO/${String(Number(soId || 0)).padStart(4, '0')}` : '-';

const formatMode = (mode?: string) => {
    const normalized = String(mode || '').toUpperCase();
    if (normalized === 'CUSTOMER_PREPAYMENT') return 'Customer Prepayment';
    if (normalized === 'INVOICE_SETTLEMENT') return 'Invoice Settlement';
    if (normalized === 'LEGACY_UNAPPLIED') return 'Unapplied Receipt';
    return normalized || '-';
};

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
};

const PAGE_SIZE = 50;

export function ARReceiptRegister() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<ArReceiptSummary[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        customer_id: 'all',
        mode: 'all',
    });

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadRows = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : rows.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        try {
            const result = await api.financials.listArReceipts({
                q: debouncedQ || undefined,
                customer_id: state.customer_id !== 'all' ? Number(state.customer_id) : undefined,
                mode: state.mode !== 'all' ? state.mode : undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setRows((prev) => (reset ? (result.items || []) : [...prev, ...(result.items || [])]));
            setTotalCount(Number(result.total || 0));
            setHasMore(Boolean(result.has_more));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load receipt register');
            if (reset) {
                setRows([]);
                setTotalCount(0);
                setHasMore(false);
            }
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            } else {
                setLoadingMore(false);
            }
        }
    };

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch(() => setCustomers([]));
    }, []);

    useEffect(() => {
        loadRows(true);
    }, [debouncedQ, state.customer_id, state.mode]);

    const columns = useMemo(
        () => [
            {
                header: 'Receipt',
                accessor: (row: ArReceiptSummary) => (
                    <div className="space-y-1">
                        <div className="font-mono font-medium text-slate-900">{formatReceiptNumber(row.receipt_id)}</div>
                        <div className="text-xs text-slate-500">{formatDate(row.receipt_date)}</div>
                    </div>
                ),
            },
            {
                header: 'Customer',
                accessor: (row: ArReceiptSummary) => (
                    <div className="space-y-1">
                        <div className="font-medium text-slate-900">{row.customer_name || `Customer #${row.customer_id}`}</div>
                        <div className="text-xs text-slate-500">{formatMode(row.mode)}</div>
                    </div>
                ),
            },
            {
                header: 'Against',
                accessor: (row: ArReceiptSummary) => row.invoice_hint_id
                    ? formatInvoiceNumber(row.invoice_hint_id)
                    : row.so_id
                        ? formatSoNumber(row.so_id)
                        : 'Customer Account',
            },
            {
                header: 'Amount',
                accessor: (row: ArReceiptSummary) => `AED ${formatMoney(row.amount)}`,
                className: 'text-right',
            },
            {
                header: 'Applied',
                accessor: (row: ArReceiptSummary) => `AED ${formatMoney(row.applied_amount)}`,
                className: 'text-right',
            },
            {
                header: 'Unapplied',
                accessor: (row: ArReceiptSummary) => `AED ${formatMoney(row.unapplied_amount)}`,
                className: 'text-right',
            },
            {
                header: 'Actions',
                accessor: (row: ArReceiptSummary) => (
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/financials/receipts/${row.receipt_id}`);
                            }}
                        >
                            <Eye className="mr-2 h-4 w-4" /> View
                        </Button>
                        {String(row.mode || '').toUpperCase() === 'CUSTOMER_PREPAYMENT' && Number(row.unapplied_amount || 0) > 0.01 && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/financials/receipts/${row.receipt_id}?allocate=1`);
                                }}
                            >
                                <Link2 className="mr-2 h-4 w-4" /> Allocate
                            </Button>
                        )}
                    </div>
                ),
                className: 'text-right',
            },
        ],
        [navigate]
    );

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Receipt Register...</div>;

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-xl font-semibold">Receipt Register</h2>
                <p className="text-sm text-muted-foreground">Find posted customer receipts again and continue allocation for unapplied advances.</p>
            </div>

            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search by receipt no, journal, reference, or customer..."
                controls={[
                    {
                        key: 'customer_id',
                        label: 'Customer',
                        type: 'select',
                        value: state.customer_id,
                        onChange: (value) => setFilter('customer_id', value),
                        options: [
                            { value: 'all', label: 'All Customers' },
                            ...customers.map((customer) => ({
                                value: String(customer.customer_id),
                                label: customer.customer_name,
                            })),
                        ],
                    },
                    {
                        key: 'mode',
                        label: 'Mode',
                        type: 'select',
                        value: state.mode,
                        onChange: (value) => setFilter('mode', value),
                        options: [
                            { value: 'all', label: 'All Modes' },
                            { value: 'CUSTOMER_PREPAYMENT', label: 'Customer Prepayment' },
                            { value: 'INVOICE_SETTLEMENT', label: 'Invoice Settlement' },
                            { value: 'LEGACY_UNAPPLIED', label: 'Unapplied Receipt' },
                        ],
                    },
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={`${rows.length} of ${totalCount}`}
            />

            <CompactTable
                data={rows}
                columns={columns}
                keyField="receipt_id"
                loading={loading}
                emptyMessage="No posted receipts found."
                onRowClick={(row) => navigate(`/financials/receipts/${row.receipt_id}`)}
            />

            {hasMore && (
                <div className="flex justify-center">
                    <Button variant="ghost" onClick={() => loadRows(false)} disabled={loadingMore}>
                        {loadingMore ? 'Loading...' : 'Load older receipts'}
                    </Button>
                </div>
            )}
        </div>
    );
}
